(() => {
    'use strict';

    const scriptReadyAttribute = 'data-featured-product-script-ready';
    const documentRoot = document.documentElement;

    if (documentRoot.hasAttribute(scriptReadyAttribute)) {
        return;
    }

    documentRoot.setAttribute(scriptReadyAttribute, '');

    const instances = new Map();

    const strings = {
        unavailable:
            documentRoot.dataset.productUnavailable || '',
        addedToCart:
            documentRoot.dataset.addedToCart || '',
        generalError:
            documentRoot.dataset.cartGeneralError || '',
        optionUpdateError:
            documentRoot.dataset.productOptionUpdateError || '',
        quantityMinimum:
            documentRoot.dataset.productQuantityMinimum || '',
        quantityMaximum:
            documentRoot.dataset.productQuantityMaximum || '',
    };

    const interpolateQuantity = (template, quantity) =>
        String(template || '').replace('__QUANTITY__', String(quantity));

    const shopifyRoot = () => {
        const root = window.Shopify?.routes?.root || '/';

        return root.endsWith('/') ? root : `${root}/`;
    };

    const initPaymentButtons = () => {
        window.Shopify?.PaymentButton?.init?.();
    };

    const reactivateScripts = (root) => {
        root?.querySelectorAll('script').forEach((oldScript) => {
            const newScript = document.createElement('script');

            [...oldScript.attributes].forEach((attribute) => {
                newScript.setAttribute(attribute.name, attribute.value);
            });

            newScript.textContent = oldScript.textContent;
            oldScript.replaceWith(newScript);
        });
    };

    const dispatchThemeEvent = (name, detail) => {
        document.dispatchEvent(
            new CustomEvent(name, {
                bubbles: true,
                detail,
            })
        );
    };

    class FeaturedProduct {
        constructor(section) {
            this.section = section;
            this.sectionId = section.dataset.sectionId || '';
            this.productUrl = section.dataset.productUrl || '';

            this.requestController = null;
            this.requestSequence = 0;
            this.submitting = false;
            this.feedbackTimer = null;
            this.addButtonWasDisabled = false;

            this.cartAddUrl = `${shopifyRoot()}cart/add.js`;
            this.cartJsonUrl = `${shopifyRoot()}cart.js`;

            this.handleClick = this.handleClick.bind(this);
            this.handleChange = this.handleChange.bind(this);
            this.handleKeydown = this.handleKeydown.bind(this);
            this.handleSubmit = this.handleSubmit.bind(this);

            this.refreshElements();
        }

        refreshElements() {
            if (!this.section) {
                return;
            }

            this.mediaRoot = this.section.querySelector(
                '[data-featured-product-media]'
            );

            this.mediaItems = [
                ...this.section.querySelectorAll('[data-featured-media-item]'),
            ];

            this.thumbnails = [
                ...this.section.querySelectorAll('[data-featured-thumbnail]'),
            ];

            this.mediaStatus = this.section.querySelector(
                '[data-featured-media-status]'
            );

            this.variantPicker = this.section.querySelector(
                '[data-product-variant-picker]'
            );

            this.form = this.section.querySelector(
                '[data-featured-product-form]'
            );

            this.variantIdInput =
                this.form?.querySelector('[data-featured-variant-id]') || null;

            this.quantityInput =
                this.form?.querySelector('[data-featured-quantity-input]') || null;

            this.quantityMinus =
                this.form?.querySelector('[data-featured-quantity-minus]') || null;

            this.quantityPlus =
                this.form?.querySelector('[data-featured-quantity-plus]') || null;

            this.quantityMessage =
                this.form?.querySelector('[data-featured-quantity-message]') || null;

            this.addButton =
                this.form?.querySelector('[data-featured-add-to-cart]') || null;

            this.feedback =
                this.form?.querySelector('[data-featured-cart-feedback]') || null;

            this.activeMediaIndex = this.getInitialMediaIndex();
        }

        init() {
            if (
                !this.section ||
                this.section.dataset.featuredProductBound === 'true'
            ) {
                return;
            }

            this.section.dataset.featuredProductBound = 'true';

            this.section.addEventListener('click', this.handleClick);
            this.section.addEventListener('change', this.handleChange);
            this.section.addEventListener('keydown', this.handleKeydown);
            this.section.addEventListener('submit', this.handleSubmit);

            this.syncSelectedLabels();
            this.updateQuantity({ announce: false });

            this.showMedia(this.activeMediaIndex, {
                initial: true,
            });

            initPaymentButtons();
        }

        getInitialMediaIndex() {
            const thumbnailIndex = this.thumbnails.findIndex(
                (thumbnail) =>
                    thumbnail.classList.contains('is-active') ||
                    thumbnail.getAttribute('aria-selected') === 'true'
            );

            if (thumbnailIndex >= 0) {
                return thumbnailIndex;
            }

            const mediaIndex = this.mediaItems.findIndex((item) =>
                item.classList.contains('is-active')
            );

            return mediaIndex >= 0 ? mediaIndex : 0;
        }

        normalizeMediaIndex(index) {
            if (!this.mediaItems.length || !Number.isInteger(index)) {
                return null;
            }

            if (index < 0) {
                return this.mediaItems.length - 1;
            }

            if (index >= this.mediaItems.length) {
                return 0;
            }

            return index;
        }

        showMedia(
            index,
            {
                initial = false,
                focus = false,
            } = {}
        ) {
            const normalized = this.normalizeMediaIndex(index);

            if (normalized === null) {
                return;
            }

            const previous = this.activeMediaIndex;

            this.activeMediaIndex = normalized;

            this.mediaItems.forEach((item, itemIndex) => {
                const active = itemIndex === normalized;

                item.hidden = !active;
                item.classList.toggle('is-active', active);
                item.setAttribute('aria-hidden', String(!active));
            });

            this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
                const active = thumbnailIndex === normalized;

                thumbnail.classList.toggle('is-active', active);
                thumbnail.setAttribute('aria-selected', String(active));

                if (active && thumbnail.tabIndex !== 0) {
                    thumbnail.tabIndex = 0;
                } else if (!active && thumbnail.tabIndex !== -1) {
                    thumbnail.tabIndex = -1;
                }
            });

            if (previous !== normalized) {
                this.mediaItems[previous]
                    ?.querySelectorAll('video')
                    .forEach((video) => {
                        if (!video.paused) {
                            video.pause();
                        }
                    });
            }

            if (this.mediaStatus) {
                this.mediaStatus.textContent =
                    `${normalized + 1} / ${this.mediaItems.length}`;
            }

            if (focus) {
                this.thumbnails[normalized]?.focus({
                    preventScroll: true,
                });
            }

            if (!initial && previous !== normalized) {
                dispatchThemeEvent(
                    'theme:featured-product:media-change',
                    {
                        sectionId: this.sectionId,
                        productId: this.section?.dataset.productId || null,
                        index: normalized,
                        media: this.mediaItems[normalized] || null,
                    }
                );
            }
        }

        getSelectedInputs() {
            if (!this.variantPicker) {
                return [];
            }

            return [
                ...this.variantPicker.querySelectorAll('[data-product-option]'),
            ]
                .map((group) =>
                    group.querySelector('[data-option-input]:checked')
                )
                .filter(Boolean);
        }

        getSelectedOptionValueIds() {
            return this.getSelectedInputs()
                .map((input) => input.dataset.optionValueId)
                .filter(Boolean);
        }

        syncSelectedLabels() {
            if (!this.variantPicker) {
                return;
            }

            this.variantPicker
                .querySelectorAll('[data-product-option]')
                .forEach((group) => {
                    const checked = group.querySelector(
                        '[data-option-input]:checked'
                    );

                    const output = group.querySelector(
                        '[data-option-selected-value]'
                    );

                    if (output) {
                        output.textContent = checked ? checked.value : '';
                    }
                });
        }

        buildVariantRequestUrl(targetProductUrl, optionValueIds) {
            const url = new URL(
                targetProductUrl ||
                this.productUrl ||
                window.location.pathname,
                window.location.origin
            );

            url.searchParams.delete('variant');
            url.searchParams.delete('option_values');
            url.searchParams.delete('section_id');
            url.searchParams.delete('sections');

            url.searchParams.set(
                'option_values',
                optionValueIds.join(',')
            );

            url.searchParams.set(
                'section_id',
                this.sectionId
            );

            return url;
        }

        async changeVariant(input) {
            const optionValueIds = this.getSelectedOptionValueIds();

            if (!optionValueIds.length || !this.sectionId) {
                return;
            }

            const targetProductUrl =
                input.dataset.productUrl ||
                this.variantPicker?.dataset.productUrl ||
                this.productUrl ||
                window.location.pathname;

            this.requestController?.abort();

            const controller = new AbortController();
            const sequence = ++this.requestSequence;
            const focusOptionValueId =
                input.dataset.optionValueId || '';

            const requestUrl = this.buildVariantRequestUrl(
                targetProductUrl,
                optionValueIds
            );

            this.requestController = controller;
            this.section.setAttribute('aria-busy', 'true');

            try {
                const response = await fetch(requestUrl.toString(), {
                    method: 'GET',
                    headers: {
                        Accept: 'text/html',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    credentials: 'same-origin',
                    signal: controller.signal,
                });

                if (!response.ok) {
                    throw new Error(
                        `Featured Product render failed (${response.status}).`
                    );
                }

                const html = await response.text();

                if (sequence !== this.requestSequence) {
                    return;
                }

                const doc = new DOMParser().parseFromString(
                    html,
                    'text/html'
                );

                const newSection = doc.querySelector(
                    `[data-featured-product-section][data-section-id="${CSS.escape(
                        this.sectionId
                    )}"]`
                );

                if (!newSection) {
                    throw new Error(
                        'Featured Product response did not contain the expected section root.'
                    );
                }

                const productChanged = this.didProductChange(
                    targetProductUrl,
                    newSection
                );

                const variantId =
                    newSection.dataset.selectedVariantId || null;

                const productId =
                    newSection.dataset.productId || null;

                this.requestController = null;

                this.applyRenderedSection(newSection, {
                    productChanged,
                    focusOptionValueId,
                });

                dispatchThemeEvent(
                    'theme:featured-product:variant-rendered',
                    {
                        sectionId: this.sectionId,
                        productId,
                        variantId,
                        productChanged,
                    }
                );
            } catch (error) {
                if (error?.name === 'AbortError') {
                    return;
                }

                console.error(
                    '[Featured Product] Variant update failed.',
                    error
                );

                this.showFeedback(
                    strings.optionUpdateError,
                    'error'
                );
            } finally {
                if (this.requestController === controller) {
                    this.requestController = null;
                    this.section?.removeAttribute('aria-busy');
                }
            }
        }

        didProductChange(targetProductUrl, newSection) {
            const oldId = String(
                this.section?.dataset.productId || ''
            );

            const newId = String(
                newSection.dataset.productId || ''
            );

            if (oldId && newId && oldId !== newId) {
                return true;
            }

            const oldUrl = new URL(
                this.productUrl || window.location.pathname,
                window.location.origin
            );

            const nextUrl = new URL(
                targetProductUrl ||
                this.productUrl ||
                window.location.pathname,
                window.location.origin
            );

            return oldUrl.pathname !== nextUrl.pathname;
        }
        replaceVariantElement(newSection, selector) {
            const current = this.section.querySelector(selector);
            const next = newSection.querySelector(selector);

            if (current && next) {
                current.replaceWith(next);
                return;
            }

            if (current && !next) {
                current.remove();
            }
        }

        applyRenderedSection(
            newSection,
            {
                productChanged,
                focusOptionValueId,
            }
        ) {
            if (productChanged) {
                const oldSection = this.section;
                const sectionId = this.sectionId;

                this.destroy();
                instances.delete(sectionId);

                oldSection.replaceWith(newSection);

                reactivateScripts(newSection);
                initializeSection(newSection);

                this.restoreOptionFocus(
                    newSection,
                    focusOptionValueId
                );

                return;
            }

            this.replaceVariantElement(
                newSection,
                '[data-featured-product-media]'
            );

            this.replaceVariantElement(
                newSection,
                '[data-featured-block-type="price"]'
            );

            this.replaceVariantElement(
                newSection,
                '[data-featured-block-type="variant_picker"]'
            );

            this.replaceVariantElement(
                newSection,
                '[data-featured-block-type="sku"]'
            );

            this.replaceVariantElement(
                newSection,
                '[data-featured-block-type="buy_buttons"]'
            );

            this.section.dataset.productId =
                newSection.dataset.productId || '';

            this.section.dataset.productUrl =
                newSection.dataset.productUrl || '';

            this.productUrl =
                this.section.dataset.productUrl ||
                this.productUrl;

            if (newSection.dataset.selectedVariantId) {
                this.section.dataset.selectedVariantId =
                    newSection.dataset.selectedVariantId;
            } else {
                delete this.section.dataset.selectedVariantId;
            }

            this.section.removeAttribute('aria-busy');

            this.refreshElements();
            this.syncSelectedLabels();
            this.updateQuantity({ announce: false });

            this.showMedia(this.activeMediaIndex, {
                initial: true,
            });

            initPaymentButtons();

            this.restoreOptionFocus(
                this.section,
                focusOptionValueId
            );
        }

        restoreOptionFocus(section, optionValueId) {
            if (!section || !optionValueId) {
                return;
            }

            const input = [
                ...section.querySelectorAll(
                    '[data-option-input][data-option-value-id]'
                ),
            ].find(
                (candidate) =>
                    candidate.dataset.optionValueId ===
                    optionValueId
            );

            requestAnimationFrame(() => {
                input?.focus({
                    preventScroll: true,
                });
            });
        }

        quantityMin() {
            const value = Number(this.quantityInput?.min);

            return Number.isFinite(value) ? value : 1;
        }

        quantityMax() {
            const raw = this.quantityInput?.getAttribute('max');

            if (raw === null || raw === '') {
                return null;
            }

            const value = Number(raw);

            return Number.isFinite(value) ? value : null;
        }

        quantityStep() {
            const value = Number(this.quantityInput?.step);

            return Number.isFinite(value) && value > 0
                ? value
                : 1;
        }

        stepQuantity(direction) {
            if (!this.quantityInput) {
                return;
            }

            const current = Number(this.quantityInput.value);

            const value = Number.isFinite(current)
                ? current
                : this.quantityMin();

            this.quantityInput.value = String(
                value + direction * this.quantityStep()
            );

            this.updateQuantity();
        }

        updateQuantity(
            {
                announce = true,
            } = {}
        ) {
            if (!this.quantityInput) {
                return true;
            }

            const min = this.quantityMin();
            const max = this.quantityMax();
            const step = this.quantityStep();
            const raw = Number(this.quantityInput.value);

            let value = Number.isFinite(raw) ? raw : min;
            let message = '';

            if (value < min) {
                value = min;
                message = interpolateQuantity(strings.quantityMinimum, min);
            }

            if (max !== null && value > max) {
                value = max;
                message = interpolateQuantity(strings.quantityMaximum, max);
            }

            const increments = Math.round(
                (value - min) / step
            );

            value = min + increments * step;

            if (value < min) {
                value = min;
            }

            if (max !== null && value > max) {
                value = max;
            }

            this.quantityInput.value = String(value);

            if (this.quantityMinus) {
                this.quantityMinus.disabled = value <= min;
            }

            if (this.quantityPlus) {
                this.quantityPlus.disabled =
                    max !== null && value >= max;
            }

            if (announce && this.quantityMessage) {
                this.quantityMessage.textContent = message;

                this.quantityMessage.classList.toggle(
                    'is-warning',
                    Boolean(message)
                );
            }

            return !message;
        }

        async handleSubmit(event) {
            const form = event.target.closest(
                '[data-featured-product-form]'
            );

            if (
                !form ||
                !this.section.contains(form)
            ) {
                return;
            }

            event.preventDefault();

            if (this.submitting) {
                return;
            }

            if (this.updateQuantity() === false) {
                return;
            }

            this.form = form;

            this.variantIdInput = form.querySelector(
                '[data-featured-variant-id]'
            );

            this.addButton = form.querySelector(
                '[data-featured-add-to-cart]'
            );

            this.feedback = form.querySelector(
                '[data-featured-cart-feedback]'
            );

            if (!this.variantIdInput?.value) {
                this.showFeedback(
                    strings.unavailable,
                    'error'
                );

                return;
            }

            this.submitting = true;
            this.setAddButtonLoading(true);
            this.clearFeedback();

            try {
                const response = await fetch(this.cartAddUrl, {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                    },
                    body: new FormData(form),
                });

                const item = await response.json();

                if (!response.ok) {
                    throw item;
                }

                this.showFeedback(
                    strings.addedToCart,
                    'success'
                );

                dispatchThemeEvent(
                    'theme:cart:add',
                    {
                        sectionId: this.sectionId,
                        productId:
                            this.section?.dataset.productId || null,
                        source: 'featured-product',
                        item,
                    }
                );

                this.refreshCart();
            } catch (error) {
                this.showFeedback(
                    error?.description ||
                    error?.message ||
                    strings.generalError,
                    'error'
                );
            } finally {
                this.submitting = false;
                this.setAddButtonLoading(false);
            }
        }

        setAddButtonLoading(loading) {
            if (!this.addButton) {
                return;
            }

            if (loading) {
                this.addButtonWasDisabled =
                    this.addButton.disabled;

                this.addButton.disabled = true;
                this.addButton.classList.add('is-loading');

                return;
            }

            this.addButton.classList.remove('is-loading');
            this.addButton.disabled =
                this.addButtonWasDisabled;
        }

        async refreshCart() {
            try {
                const response = await fetch(this.cartJsonUrl, {
                    headers: {
                        Accept: 'application/json',
                    },
                });

                if (!response.ok) {
                    return;
                }

                const cart = await response.json();

                dispatchThemeEvent(
                    'theme:cart:update',
                    {
                        cart,
                    }
                );

                document
                    .querySelectorAll('[data-cart-count]')
                    .forEach((element) => {
                        element.textContent =
                            String(cart.item_count);
                    });
            } catch (_) {
                // Add to cart already succeeded; cart refresh is secondary.
            }
        }
        showFeedback(message, type) {
            if (!this.feedback) {
                return;
            }

            if (this.feedbackTimer) {
                window.clearTimeout(this.feedbackTimer);
            }

            this.feedback.textContent = message;

            this.feedback.classList.remove(
                'is-success',
                'is-error'
            );

            this.feedback.classList.add(
                'is-visible',
                type === 'error'
                    ? 'is-error'
                    : 'is-success'
            );

            if (type !== 'error') {
                this.feedbackTimer = window.setTimeout(
                    () => this.clearFeedback(),
                    4000
                );
            }
        }

        clearFeedback() {
            if (!this.feedback) {
                return;
            }

            if (this.feedbackTimer) {
                window.clearTimeout(this.feedbackTimer);
            }

            this.feedbackTimer = null;
            this.feedback.textContent = '';

            this.feedback.classList.remove(
                'is-visible',
                'is-success',
                'is-error'
            );
        }

        handleClick(event) {
            const thumbnail = event.target.closest(
                '[data-featured-thumbnail]'
            );

            if (
                thumbnail &&
                this.section.contains(thumbnail)
            ) {
                const index = Number(
                    thumbnail.dataset.galleryIndex
                );

                if (Number.isInteger(index)) {
                    this.showMedia(index);
                }

                return;
            }

            if (
                event.target.closest(
                    '[data-featured-quantity-minus]'
                )
            ) {
                this.stepQuantity(-1);
                return;
            }

            if (
                event.target.closest(
                    '[data-featured-quantity-plus]'
                )
            ) {
                this.stepQuantity(1);
            }
        }

        handleChange(event) {
            const optionInput = event.target.closest(
                '[data-option-input]'
            );

            if (
                optionInput &&
                this.variantPicker?.contains(optionInput)
            ) {
                this.syncSelectedLabels();
                this.changeVariant(optionInput);

                return;
            }

            if (
                event.target.matches(
                    '[data-featured-quantity-input]'
                )
            ) {
                this.updateQuantity();
            }
        }

        handleKeydown(event) {
            const thumbnail = event.target.closest(
                '[data-featured-thumbnail]'
            );

            if (!thumbnail) {
                return;
            }

            if (event.key === 'ArrowLeft') {
                event.preventDefault();

                this.showMedia(
                    this.activeMediaIndex - 1,
                    {
                        focus: true,
                    }
                );

                return;
            }

            if (event.key === 'ArrowRight') {
                event.preventDefault();

                this.showMedia(
                    this.activeMediaIndex + 1,
                    {
                        focus: true,
                    }
                );

                return;
            }

            if (event.key === 'Home') {
                event.preventDefault();

                this.showMedia(0, {
                    focus: true,
                });

                return;
            }

            if (event.key === 'End') {
                event.preventDefault();

                this.showMedia(
                    this.mediaItems.length - 1,
                    {
                        focus: true,
                    }
                );
            }
        }

        destroy() {
            if (this.section) {
                this.section.removeEventListener(
                    'click',
                    this.handleClick
                );

                this.section.removeEventListener(
                    'change',
                    this.handleChange
                );

                this.section.removeEventListener(
                    'keydown',
                    this.handleKeydown
                );

                this.section.removeEventListener(
                    'submit',
                    this.handleSubmit
                );

                delete this.section.dataset
                    .featuredProductBound;
            }

            this.requestController?.abort();

            if (this.feedbackTimer) {
                window.clearTimeout(this.feedbackTimer);
            }

            this.requestController = null;
            this.section = null;
        }
    }

    const initializeSection = (section) => {
        if (!section) {
            return;
        }

        const sectionId = section.dataset.sectionId;

        if (!sectionId) {
            return;
        }

        instances.get(sectionId)?.destroy();

        const instance = new FeaturedProduct(section);

        instance.init();
        instances.set(sectionId, instance);
    };

    const destroySection = (sectionId) => {
        const instance = instances.get(sectionId);

        if (!instance) {
            return;
        }

        instance.destroy();
        instances.delete(sectionId);
    };

    const initializeAll = () => {
        document
            .querySelectorAll(
                '[data-featured-product-section][data-section-id]'
            )
            .forEach(initializeSection);
    };

    if (document.readyState === 'loading') {
        document.addEventListener(
            'DOMContentLoaded',
            initializeAll,
            {
                once: true,
            }
        );
    } else {
        initializeAll();
    }

    document.addEventListener(
        'shopify:section:load',
        (event) => {
            const section =
                event.target.matches?.(
                    '[data-featured-product-section]'
                )
                    ? event.target
                    : event.target.querySelector?.(
                        '[data-featured-product-section]'
                    );

            if (section) {
                initializeSection(section);
            }
        }
    );

    document.addEventListener(
        'shopify:section:unload',
        (event) => {
            destroySection(
                event.detail?.sectionId
            );
        }
    );
})();